import {
  ActionPlanItem,
  Employee,
  FeedbackStatus,
  PopulatedFeedbackInteraction,
  UpdateActionPlanRequest,
} from '@momentum/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Textarea } from '@/shared/components/ui/Textarea';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { DatePickerInput } from '@/shared/components/inputs/DatePicker.input';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useCallback, useEffect, useState } from 'react';
import { Plus, FileText, X, Loader2, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getEmployeeRoleDisplayName } from '@/shared/utils/role-utils';
import { useUpdateActionPlan } from '@/shared/hooks';

interface ActionPlanProps {
  feedback?: PopulatedFeedbackInteraction;
  feedbackCreator: Employee;
  feedbackReceiver: Employee;
}

interface UserActionPlanCardProps {
  user: Employee;
  tasks: ActionPlanItem[];
  isFinished: boolean;
  isDisabled?: boolean;
  otherUser?: Employee;
  onTasksChange: (tasks: ActionPlanItem[]) => void;
}

function UserActionPlanCard({
  user,
  tasks,
  isFinished,
  isDisabled = false,
  otherUser,
  onTasksChange,
}: UserActionPlanCardProps) {
  const { t, i18n } = useTranslation('openBlockage');
  const { t: tCommon } = useTranslation('common');

  const addTask = () => {
    const newId = Date.now().toString();
    const newTask: ActionPlanItem = {
      userId: user._id,
      id: newId,
      text: '',
      dueDate: undefined,
    };
    onTasksChange([...tasks, newTask]);
  };

  const removeTask = (taskId: string) => {
    onTasksChange(tasks.filter((task) => task.id !== taskId));
  };

  const updateTask = (taskId: string, updates: Partial<ActionPlanItem>) => {
    onTasksChange(
      tasks.map((task) => (task.id === taskId ? { ...task, ...updates } : task))
    );
  };

  const getUserDisplayName = (user: Employee) => {
    return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
  };

  return (
    <Card className="h-full flex flex-col shadow-xl">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <UserSelectorAvatar
            imageUrl={user.personalInfo.imageUrl}
            fullName={getUserDisplayName(user)}
            size="lg"
          />
          <div>
            <h3 className="text-lg font-semibold text-primary">
              {getUserDisplayName(user)}
            </h3>
            <p className="text-sm text-muted-foreground">
              {getEmployeeRoleDisplayName(user.role, tCommon)}
            </p>
          </div>
        </div>
      </CardHeader>

      {!isDisabled ? (
        <>
          <CardContent className="flex-1 flex flex-col">
            <div className="space-y-4 flex-1">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`border rounded-lg p-4 space-y-3 ${
                    !task.text.trim() && !task.dueDate
                      ? 'border-2 border-dashed border-muted-foreground/25'
                      : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Textarea
                        value={task.text}
                        onChange={(e) =>
                          updateTask(task.id, { text: e.target.value })
                        }
                        disabled={isFinished}
                        placeholder={t('enterTaskDescription')}
                        className="min-h-[60px] resize-none"
                      />
                      {!isFinished ? (
                        <DatePickerInput
                          value={task.dueDate ? new Date(task.dueDate) : null}
                          onChange={(date) =>
                            updateTask(task.id, {
                              dueDate: date?.toISOString(),
                            })
                          }
                          placeholder={t('selectDueDate')}
                          className="w-full"
                          disabled={isFinished}
                        />
                      ) : (
                        task.dueDate && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {t('due')}{' '}
                              {new Date(task.dueDate).toLocaleDateString(
                                i18n.language === 'he' ? 'he-IL' : 'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                }
                              )}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                    {!isFinished && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTask(task.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
          {!isFinished && (
            <CardFooter>
              <Button onClick={addTask}>
                <Plus className="h-4 w-4 me-2" />
                {t('addTask')}
              </Button>
            </CardFooter>
          )}
        </>
      ) : (
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-2 px-6 py-8">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-muted p-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('waitingForOtherPersonToAddTasks')(
                getUserDisplayName(otherUser ?? user)
              )}
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function ActionPlan({
  feedback,
  feedbackCreator,
  feedbackReceiver,
}: ActionPlanProps) {
  const { t, i18n } = useTranslation('openBlockage');
  const employee = useEmployeeContext();
  const updateActionPlanMutation = useUpdateActionPlan();

  const filterEmptyTasks = (tasks: ActionPlanItem[]) =>
    tasks.filter((task) => task.text.trim() !== '');

  const formatDate = (date: Date | string) => {
    const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';

    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getUserTasks = useCallback(
    (userId: string): ActionPlanItem[] => {
      if (!feedback) return [];

      const taskItems =
        feedback.tasks
          ?.filter((t) => t.userId === userId)
          .map(({ userId, task }) => ({
            id: task._id,
            userId,
            text: task.title,
            dueDate: task.dueDate,
          })) ?? [];

      const signalItems =
        feedback.signals
          ?.filter((l) => l.userId === userId)
          .map(({ userId, signal }) => ({
            id: signal._id,
            userId,
            text: signal.title,
          })) ?? [];

      return [...taskItems, ...signalItems];
    },
    [feedback]
  );

  const [receiverTasks, setReceiverTasks] = useState<ActionPlanItem[]>([]);
  const [creatorTasks, setCreatorTasks] = useState<ActionPlanItem[]>([]);

  useEffect(() => {
    setReceiverTasks(getUserTasks(feedbackReceiver._id));
  }, [feedback, feedbackReceiver._id, getUserTasks]);

  useEffect(() => {
    const userTasks = getUserTasks(feedbackCreator._id);
    setCreatorTasks(
      userTasks.length > 0
        ? userTasks
        : [
            {
              userId: feedbackCreator._id,
              id: Date.now().toString(),
              text: '',
              dueDate: undefined,
            },
          ]
    );
  }, [feedback, getUserTasks, feedbackCreator._id]);

  const isFinished = feedback?.status === FeedbackStatus.FINISHED;

  const feedbackCreatorName =
    feedbackCreator.personalInfo.firstName +
    ' ' +
    feedbackCreator.personalInfo.lastName;

  const sendActionPlan = async () => {
    // Filter out tasks that are empty (not yet filled by user)
    const filteredCreatorTasks = filterEmptyTasks(creatorTasks);
    const filteredReceiverTasks = filterEmptyTasks(receiverTasks);

    const plan: UpdateActionPlanRequest = {
      actions: [...filteredCreatorTasks, ...filteredReceiverTasks],
    };
    if (feedback?._id) {
      await updateActionPlanMutation.mutateAsync({
        id: feedback?._id,
        data: plan,
      });
    }
  };

  const feedbackCreatorTasksCount = filterEmptyTasks(creatorTasks).length;

  return (
    <div className="space-y-6">
      <Card className="shadow-xl">
        <CardHeader>
          <CardTitle>
            {t('hi')},{' '}
            <span className="text-primary">
              {employee.personalInfo.firstName +
                ' ' +
                employee.personalInfo.lastName}
            </span>
          </CardTitle>

          <CardDescription>
            {t('hereYouCanBuild')}
            <ol className="ml-2 list-decimal list-inside mt-2">
              <li>{t('writeActionsThatHelp')(feedbackCreatorName)}</li>
              <li>{t('ifYouNeedHelp')(feedbackCreatorName)}</li>
              <li>{t('doneIsItReady')(feedbackCreatorName)}</li>
            </ol>
          </CardDescription>

          {isFinished && feedback.metadata.closedAt && (
            <h3>
              {t('closedAt')} {formatDate(feedback.metadata.closedAt)}
            </h3>
          )}

          {feedback?.status === FeedbackStatus.ACTION_PLAN_APPROVE && (
            <div
              className="mt-4 p-3 bg-amber-100 border border-amber-400 text-amber-700 rounded-lg"
              role="alert"
            >
              <p className="font-bold">{t('actionPlanPendingApprovalTitle')}</p>
            </div>
          )}
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
        {/* Employee's Action Plan */}
        <UserActionPlanCard
          user={feedbackReceiver}
          tasks={receiverTasks}
          isFinished={isFinished}
          onTasksChange={setReceiverTasks}
        />

        {/* Feedback Creator's Action Plan */}
        <UserActionPlanCard
          user={feedbackCreator}
          tasks={creatorTasks}
          isFinished={isFinished}
          isDisabled={feedbackCreatorTasksCount === 0}
          otherUser={feedbackReceiver}
          onTasksChange={setCreatorTasks}
        />
      </div>

      {/* Send Action Plan Button */}
      {!isFinished && (
        <div className="flex justify-center">
          <Button
            size="lg"
            className="px-8"
            onClick={sendActionPlan}
            disabled={
              updateActionPlanMutation.isPending ||
              (feedbackCreatorTasksCount === 0 &&
                filterEmptyTasks(receiverTasks).length === 0)
            }
          >
            {updateActionPlanMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {feedback?.status === FeedbackStatus.ACTION_PLAN_APPROVE &&
            feedbackCreator._id.toString() === employee._id.toString()
              ? t('sendApproveActionPlan')
              : t('sendActionPlan')}
          </Button>
        </div>
      )}
    </div>
  );
}
